import React, { useEffect } from 'react';
import CardMedia from '../CardMedia';
import Card from '../Card';
import { Play, Pause, HeartFilled, Heart } from '@octanner/prism-icons';
import Typography from '../Typography';
import {
  StyledCard,
  StyledCardActionArea,
  PlayButton,
  MediaCardContent,
  MediaCardWrapper,
  StyledBrokenImage,
  ActionButtonContainer,
  FavoriteButtonSmall,
} from './styles';
import { useTranslation } from 'react-i18next';
import { getPreferredMediaUrl } from './MediaLibrary';
import { Media, MediaCardProps } from './types';

const MediaCard: React.FC<
  React.ComponentProps<typeof Card> & MediaCardProps
> = ({
  setSelected,
  title,
  onMediaSelect,
  onFavorite,
  media,
  size,
  isFavorite = () => false,
}) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [paused, setPaused] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [favoriteLoading, setFavoriteLoading] = React.useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!paused) {
      if (videoRef?.current?.play) {
        videoRef?.current?.play();
      }
    } else {
      if (videoRef?.current?.pause) {
        videoRef?.current?.pause();
      }
    }
  }, [paused]);

  const onCardPlayClick = () => {
    setPaused(!paused);
  };

  const onCardSelect = (item: Media | null) => {
    if (onMediaSelect) {
      onMediaSelect(item);
    }
    setSelected(item);
  };

  const imageMedia = (
    <CardMedia
      component={media.mediaType}
      playsInline
      controlsList="nodownload"
      disablePictureInPicture
      data-testid="video"
      src={
        getPreferredMediaUrl(media.thumbNailUrl, media.normalUrl) ||
        media.largeUrl
      }
      title={title}
      onError={() => {
        setError(true);
      }}
    />
  );

  return (
    <MediaCardWrapper>
      <StyledCard
        className="prism-media-card"
        isError={error}
        tabIndex={0}
        onClick={() => onCardSelect(media)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCardSelect(media);
          }
        }}
      >
        {error ? (
          <MediaCardContent>
            {size !== 'auto' && <StyledBrokenImage />}
            <Typography p={2} variant={size !== 'sm' ? 'body2' : 'body1'}>
              {t('prism-header:media-error', ' This media failed to load.')}
            </Typography>
          </MediaCardContent>
        ) : (
          <StyledCardActionArea
            tabIndex={-1}
            data-testid="media-library-card"
            aria-label={
              media.mediaType === 'img'
                ? `select ${media.title}`
                : `select ${media.title} video`
            }
          >
            {media.mediaType === 'img' ? (
              imageMedia
            ) : (
              <CardMedia
                ref={videoRef}
                component={media.mediaType}
                playsInline
                controlsList="nodownload"
                disablePictureInPicture
                data-testid="video"
                src={media.mediaUrl}
                title={title}
                poster={media.largeUrl || media.thumbNailUrl || media.normalUrl}
              />
            )}
          </StyledCardActionArea>
        )}
        {onFavorite && (
          <ActionButtonContainer>
            <FavoriteButtonSmall
              size="small"
              isFavorite={isFavorite?.(media)}
              disableRipple
              disableFocusRipple
              color="white"
              loading={favoriteLoading}
              aria-label={
                isFavorite?.(media)
                  ? t('prism-header:unfavorite', 'Unfavorite')
                  : t('prism-header:favorite', 'Favorite')
              }
              onClick={(e) => {
                e.stopPropagation();

                const promise = onFavorite(media);
                if (promise?.then) {
                  setFavoriteLoading(true);
                  promise.finally?.(() => setFavoriteLoading(false));
                }
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
            >
              {isFavorite?.(media) ? <HeartFilled /> : <Heart />}
            </FavoriteButtonSmall>
          </ActionButtonContainer>
        )}
        {media.mediaType === 'video' && (
          <PlayButton
            tabIndex={0}
            disableRipple
            disableFocusRipple
            onClick={(e) => {
              e.stopPropagation();
              onCardPlayClick();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            aria-label={
              paused
                ? `play ${media.title} video`
                : `pause ${media.title} video`
            }
            aria-live="polite"
          >
            {paused ? <Play /> : <Pause />}
          </PlayButton>
        )}
      </StyledCard>
    </MediaCardWrapper>
  );
};

export default MediaCard;
